// Module-level singleton: the search index is fetched once per locale per session
// and held in memory so the palette, hub, and category pages all share it.
// NOTE: MiniSearch is not installed in this project. This composable implements a
// lightweight scored text-matching search over the flat index. To upgrade to
// MiniSearch, add it to package.json and replace the `scoreEntry` logic below.

import { ref, computed } from 'vue'
import { fetchDocSearchIndex } from '@/api/docs'
import type { DocSearchEntry, DocSearchResult } from '@/types/docs'

// Per-locale singletons — keyed by locale string
const indexByLocale: Record<string, DocSearchEntry[]> = {}
const loadingByLocale: Record<string, boolean> = {}
const errorByLocale: Record<string, boolean> = {}

// Reactive shims so components can observe loading/error state
const _loading = ref(false)
const _error = ref(false)

export function useDocSearch() {
  async function ensureLoaded(locale: string): Promise<void> {
    if (indexByLocale[locale] !== undefined) return
    if (loadingByLocale[locale]) return

    loadingByLocale[locale] = true
    _loading.value = true
    _error.value = false

    try {
      indexByLocale[locale] = await fetchDocSearchIndex(locale)
    } catch {
      errorByLocale[locale] = true
      _error.value = true
    } finally {
      loadingByLocale[locale] = false
      _loading.value = false
    }
  }

  function search(query: string, locale: string): DocSearchResult[] {
    const index = indexByLocale[locale]
    if (!index || query.trim().length === 0) return []

    const terms = query
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter((t) => t.length > 0)

    const results: DocSearchResult[] = []

    for (const entry of index) {
      const score = scoreEntry(entry, terms)
      if (score > 0) {
        results.push({ ...entry, score })
      }
    }

    // Sort descending by score, then alphabetically by title for ties
    results.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return a.title.localeCompare(b.title)
    })

    return results.slice(0, 20)
  }

  const loading = computed(() => _loading.value)
  const error = computed(() => _error.value)

  return { ensureLoaded, search, loading, error }
}

function scoreEntry(entry: DocSearchEntry, terms: string[]): number {
  let score = 0

  const titleLower = entry.title.toLowerCase()
  const excerptLower = (entry.excerpt ?? '').toLowerCase()
  const headingsLower = entry.headings.map((h) => h.toLowerCase()).join(' ')
  const tagsLower = entry.tags.map((t) => t.toLowerCase()).join(' ')

  for (const term of terms) {
    if (titleLower.includes(term)) score += 10
    if (excerptLower.includes(term)) score += 5
    if (headingsLower.includes(term)) score += 3
    if (tagsLower.includes(term)) score += 4
  }

  return score
}
